import { useLocation } from "react-router-dom";
import { useTheme } from "@/context/ThemeProvider";

// ─── Keyframe animations ────────────────────────────────────────────────────
const STYLE = `
  @keyframes bg-drift-a { from { transform: translateX(110vw); } to { transform: translateX(-20vw); } }
  @keyframes bg-drift-b { from { transform: translateX(115vw); } to { transform: translateX(-18vw); } }
  @keyframes bg-cloud-slow  { 0% { transform: translateX(0); }   100% { transform: translateX(-80px); } }
  @keyframes bg-cloud-mid   { 0% { transform: translateX(0); }   100% { transform: translateX(-130px); } }
  @keyframes bg-cloud-fast  { 0% { transform: translateX(0); }   100% { transform: translateX(-55px); } }
  @keyframes bg-wave-drift  { 0% { transform: translateX(0); }   100% { transform: translateX(72px); } }
  @keyframes bg-sun-pulse   { 0%,100% { opacity: .65; }  50% { opacity: 1; } }
  @keyframes bg-moon-glow   { 0%,100% { opacity: .55; }  50% { opacity: .9; } }
  @keyframes bg-shimmer     { 0%,100% { opacity: .35; }  50% { opacity: .85; } }
  .bg-cloud-slow  { animation: bg-cloud-slow  130s linear infinite; }
  .bg-cloud-mid   { animation: bg-cloud-mid    95s linear infinite; }
  .bg-cloud-fast  { animation: bg-cloud-fast  165s linear infinite; }
  .bg-wave-drift  { animation: bg-wave-drift   22s ease-in-out infinite alternate; }
  .bg-sun-pulse   { animation: bg-sun-pulse     9s ease-in-out infinite; }
  .bg-moon-glow   { animation: bg-moon-glow     7s ease-in-out infinite; }
  .bg-shimmer     { animation: bg-shimmer       4s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .bg-cloud-slow, .bg-cloud-mid, .bg-cloud-fast,
    .bg-wave-drift, .bg-sun-pulse, .bg-moon-glow,
    .bg-shimmer, [data-bg-anim] { animation: none !important; }
  }
`;

// ─── Landscape palettes ─────────────────────────────────────────────────────
// DARK = bold / dramatic (deep night). LIGHT = clean / airy (soft daytime).
// Colors carry their own alpha so the SVG stays subtle and never overwhelms text.
const DARK = {
  showStars: true,
  star: "#c8e8f8",
  skyStops: [[0, "rgba(3,13,28,1)"], [60, "rgba(6,32,64,0.95)"], [100, "rgba(12,54,96,0.85)"]],
  horizonStops: [[0, "rgba(26,96,144,0.45)"], [100, "rgba(26,96,144,0)"]],
  sunStops: [[0, "rgba(0,196,176,0.55)"], [45, "rgba(0,196,176,0.18)"], [100, "rgba(0,196,176,0)"]],
  sunDiscOuter: "rgba(0,196,176,0.30)", sunDiscMid: "rgba(0,196,176,0.50)", sunCore: "rgba(93,232,216,0.70)",
  sunRay: "#00C4B0",
  cloud: "#b8d8f0", cloudOpa: 1,
  mtnSkyStops: [[0, "rgba(11,32,64,0.9)"], [100, "rgba(13,44,80,0.75)"]],
  mtnGlowStops: [[0, "rgba(255,107,43,0.14)"], [100, "rgba(255,107,43,0)"]],
  mtnFarStops: [[0, "rgba(28,62,112,0.6)"], [100, "rgba(10,30,56,0.95)"]],
  mtnMidStops: [[0, "rgba(16,40,72,0.75)"], [100, "rgba(7,15,30,1)"]],
  mtnNearStops: [[0, "rgba(10,28,48,0.9)"], [100, "rgba(3,8,16,1)"]],
  warm: "#FF6B2B",
  snowStroke: "#e8f4ff", snowFill: "#c8dff0",
  zoneFade: "#04090f",
  forestStops: [[0, "rgba(7,22,16,0.92)"], [100, "rgba(11,32,20,0.88)"]],
  forestGlowStops: [[0, "rgba(183,255,90,0.07)"], [100, "rgba(183,255,90,0)"]],
  hillFarStops: [[0, "rgba(14,40,24,0.8)"], [100, "rgba(8,22,14,0.95)"]],
  hillNearStops: [[0, "rgba(8,18,16,0.9)"], [100, "rgba(4,12,8,1)"]],
  treeTrunk: "#050e07", tree1: "#0b2010", tree2: "#0d2614", tree3: "#0f2c18", treeOpa: 1,
  lime: "#B7FF5A",
  meadow: "#040e06",
  lakeStops: [[0, "rgba(4,21,32,0.92)"], [100, "rgba(6,36,56,0.96)"]],
  waterStops: [[0, "rgba(111,231,255,0.10)"], [100, "rgba(111,231,255,0.03)"]],
  moonStops: [[0, "rgba(111,231,255,0.65)"], [45, "rgba(111,231,255,0.20)"], [100, "rgba(111,231,255,0)"]],
  moonDiscC1: "rgba(111,231,255,0.25)", moonDiscC2: "rgba(111,231,255,0.45)", moonCore: "rgba(196,242,255,0.65)",
  farShore: "#041018",
  foam: "#6FE7FF", wave: "#6FE7FF", moonReflect: "#6FE7FF", shimmer: "#6FE7FF",
  farDistShore: "#030c14",
};

const LIGHT = {
  showStars: false,
  star: "transparent",
  skyStops: [[0, "rgba(150,190,235,0.42)"], [60, "rgba(186,214,242,0.24)"], [100, "rgba(228,240,251,0.10)"]],
  horizonStops: [[0, "rgba(150,190,235,0.30)"], [100, "rgba(150,190,235,0)"]],
  sunStops: [[0, "rgba(255,194,75,0.45)"], [45, "rgba(255,194,75,0.16)"], [100, "rgba(255,194,75,0)"]],
  sunDiscOuter: "rgba(255,194,75,0.30)", sunDiscMid: "rgba(255,205,110,0.55)", sunCore: "rgba(255,236,190,0.85)",
  sunRay: "#F0A93C",
  cloud: "#ffffff", cloudOpa: 6,
  mtnSkyStops: [[0, "rgba(206,222,240,0.30)"], [100, "rgba(224,234,246,0.16)"]],
  mtnGlowStops: [[0, "rgba(255,180,120,0.16)"], [100, "rgba(255,180,120,0)"]],
  mtnFarStops: [[0, "rgba(176,196,222,0.34)"], [100, "rgba(150,172,202,0.30)"]],
  mtnMidStops: [[0, "rgba(150,172,202,0.36)"], [100, "rgba(120,146,182,0.34)"]],
  mtnNearStops: [[0, "rgba(120,146,182,0.40)"], [100, "rgba(96,124,162,0.40)"]],
  warm: "#E8893A",
  snowStroke: "#ffffff", snowFill: "#ffffff",
  zoneFade: "#eef4fb",
  forestStops: [[0, "rgba(176,206,150,0.30)"], [100, "rgba(160,194,132,0.28)"]],
  forestGlowStops: [[0, "rgba(140,200,80,0.10)"], [100, "rgba(140,200,80,0)"]],
  hillFarStops: [[0, "rgba(168,200,140,0.34)"], [100, "rgba(148,184,120,0.34)"]],
  hillNearStops: [[0, "rgba(140,178,112,0.40)"], [100, "rgba(120,162,96,0.42)"]],
  treeTrunk: "#8a7a60", tree1: "#7aa066", tree2: "#88ab73", tree3: "#97b982", treeOpa: 0.6,
  lime: "#6fae3a",
  meadow: "#bcd6a0",
  lakeStops: [[0, "rgba(170,214,234,0.30)"], [100, "rgba(150,200,224,0.34)"]],
  waterStops: [[0, "rgba(120,200,225,0.16)"], [100, "rgba(120,200,225,0.05)"]],
  moonStops: [[0, "rgba(255,214,130,0.45)"], [45, "rgba(255,214,130,0.16)"], [100, "rgba(255,214,130,0)"]],
  moonDiscC1: "rgba(255,214,130,0.30)", moonDiscC2: "rgba(255,224,150,0.50)", moonCore: "rgba(255,244,210,0.80)",
  farShore: "#aac3d6",
  foam: "#4aa6c8", wave: "#5fb4d4", moonReflect: "#ffd98a", shimmer: "#7fc8e0",
  farDistShore: "#bcd2e0",
};

// Gradient helpers — colors carry their own alpha (no stopOpacity needed).
const Lin = ({ id, stops }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    {stops.map(([o, col], i) => <stop key={i} offset={`${o}%`} stopColor={col} />)}
  </linearGradient>
);
const Rad = ({ id, cx, cy, r, stops }) => (
  <radialGradient id={id} cx={cx} cy={cy} r={r}>
    {stops.map(([o, col], i) => <stop key={i} offset={`${o}%`} stopColor={col} />)}
  </radialGradient>
);

// ─── Home: tall scrolling landscape (palette-driven) ────────────────────────
// position:absolute so it scrolls with page content.
// Zones (viewBox 0 0 1440 3200): Sky → Mountains → Forest/Field → Lake
const Landscape = ({ c }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 1440 3200"
    preserveAspectRatio="xMidYMin meet"
    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "auto", pointerEvents: "none" }}
  >
    <defs>
      <Lin id="g-sky-bg" stops={c.skyStops} />
      <Rad id="g-sun" cx="50%" cy="50%" r="50%" stops={c.sunStops} />
      <Rad id="g-horizon-glow" cx="50%" cy="100%" r="60%" stops={c.horizonStops} />
      <Lin id="g-mtn-sky" stops={c.mtnSkyStops} />
      <Lin id="g-mtn-far" stops={c.mtnFarStops} />
      <Lin id="g-mtn-mid" stops={c.mtnMidStops} />
      <Lin id="g-mtn-near" stops={c.mtnNearStops} />
      <Lin id="g-mtn-glow" stops={c.mtnGlowStops} />
      <Lin id="g-forest-bg" stops={c.forestStops} />
      <Lin id="g-hill-far" stops={c.hillFarStops} />
      <Lin id="g-hill-near" stops={c.hillNearStops} />
      <Rad id="g-forest-glow" cx="50%" cy="0%" r="70%" stops={c.forestGlowStops} />
      <Lin id="g-lake-bg" stops={c.lakeStops} />
      <Lin id="g-lake-water" stops={c.waterStops} />
      <Rad id="g-moon" cx="50%" cy="50%" r="50%" stops={c.moonStops} />
      <filter id="f-soft"><feGaussianBlur stdDeviation="4" /></filter>
    </defs>

    {/* ══ SKY ZONE  y 0 – 800 ══ */}
    <rect x="0" y="0" width="1440" height="860" fill="url(#g-sky-bg)" />

    {c.showStars && [
      [110,52],[350,40],[590,72],[830,30],[1060,58],[1300,46],[190,125],[450,108],[710,140],
      [960,88],[1210,118],[1390,132],[75,195],[310,175],[550,215],[790,168],[1040,200],[1360,178],
      [155,270],[415,255],[658,295],[905,248],[1148,272],[44,332],[260,318],[500,345],[750,305],
    ].map(([x,y],i) => (
      <circle
        key={i} cx={x} cy={y}
        r={i%5===0 ? 1.4 : i%3===0 ? 0.9 : 0.6}
        fill={c.star} fillOpacity={0.25 + (i*11%28)/100}
        className="bg-shimmer"
        style={{ animationDelay: `${(i*0.37)%4}s`, animationDuration: `${3.5+(i%4)*0.8}s` }}
      />
    ))}

    {/* Sun halo */}
    <ellipse cx="1090" cy="148" rx="140" ry="140" fill="url(#g-sun)" className="bg-sun-pulse" />
    <circle cx="1090" cy="148" r="32" fill={c.sunDiscOuter} />
    <circle cx="1090" cy="148" r="24" fill={c.sunDiscMid} />
    <circle cx="1090" cy="148" r="15" fill={c.sunCore} />
    {[0,30,60,90,120,150,180,210,240,270,300,330].map((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      const r1 = 38, r2 = 50 + (i%3)*6;
      return (
        <line key={i}
          x1={1090 + r1 * Math.cos(rad)} y1={148 + r1 * Math.sin(rad)}
          x2={1090 + r2 * Math.cos(rad)} y2={148 + r2 * Math.sin(rad)}
          stroke={c.sunRay} strokeWidth="0.7" strokeOpacity="0.25"
        />
      );
    })}

    {/* Clouds — blurred soft fills, animated */}
    <g className="bg-cloud-slow">
      <g transform="translate(90,145)" filter="url(#f-soft)" fill={c.cloud}>
        <ellipse cx="85" cy="32" rx="98" ry="30" fillOpacity={0.11 * c.cloudOpa} />
        <ellipse cx="42" cy="40" rx="58" ry="24" fillOpacity={0.09 * c.cloudOpa} />
        <ellipse cx="136" cy="38" rx="66" ry="23" fillOpacity={0.09 * c.cloudOpa} />
        <ellipse cx="82" cy="16" rx="54" ry="30" fillOpacity={0.12 * c.cloudOpa} />
        <ellipse cx="118" cy="12" rx="46" ry="27" fillOpacity={0.10 * c.cloudOpa} />
      </g>
    </g>
    <g className="bg-cloud-mid">
      <g transform="translate(550,105)" filter="url(#f-soft)" fill={c.cloud}>
        <ellipse cx="68" cy="26" rx="82" ry="27" fillOpacity={0.09 * c.cloudOpa} />
        <ellipse cx="28" cy="33" rx="50" ry="21" fillOpacity={0.08 * c.cloudOpa} />
        <ellipse cx="112" cy="31" rx="58" ry="21" fillOpacity={0.08 * c.cloudOpa} />
        <ellipse cx="65" cy="13" rx="44" ry="25" fillOpacity={0.10 * c.cloudOpa} />
      </g>
      <g transform="translate(920,195)" filter="url(#f-soft)" fill={c.cloud}>
        <ellipse cx="55" cy="21" rx="70" ry="23" fillOpacity={0.08 * c.cloudOpa} />
        <ellipse cx="22" cy="27" rx="42" ry="17" fillOpacity={0.07 * c.cloudOpa} />
        <ellipse cx="90" cy="25" rx="50" ry="18" fillOpacity={0.07 * c.cloudOpa} />
        <ellipse cx="52" cy="10" rx="36" ry="22" fillOpacity={0.09 * c.cloudOpa} />
      </g>
    </g>
    <g className="bg-cloud-fast">
      <g transform="translate(290,270)" filter="url(#f-soft)" fill={c.cloud}>
        <ellipse cx="48" cy="17" rx="60" ry="19" fillOpacity={0.07 * c.cloudOpa} />
        <ellipse cx="18" cy="23" rx="36" ry="14" fillOpacity={0.06 * c.cloudOpa} />
        <ellipse cx="78" cy="21" rx="44" ry="15" fillOpacity={0.06 * c.cloudOpa} />
      </g>
      <g transform="translate(1120,245)" filter="url(#f-soft)" fill={c.cloud}>
        <ellipse cx="52" cy="19" rx="65" ry="21" fillOpacity={0.07 * c.cloudOpa} />
        <ellipse cx="20" cy="25" rx="40" ry="15" fillOpacity={0.06 * c.cloudOpa} />
        <ellipse cx="86" cy="23" rx="48" ry="16" fillOpacity={0.06 * c.cloudOpa} />
      </g>
    </g>

    <rect x="0" y="690" width="1440" height="180" fill="url(#g-horizon-glow)" />

    {/* ══ MOUNTAIN ZONE  y 700 – 1600 ══ */}
    <rect x="0" y="700" width="1440" height="900" fill="url(#g-mtn-sky)" />
    <rect x="0" y="700" width="1440" height="320" fill="url(#g-mtn-glow)" />

    <path d="M0,1340 L90,1140 L175,1195 L295,1005 L430,1110 L570,970 L710,1070 L850,945 L990,1050 L1130,935 L1270,1040 L1380,960 L1440,990 L1440,1600 L0,1600 Z" fill="url(#g-mtn-far)" />
    <path d="M0,1440 L65,1295 L148,1350 L255,1218 L375,1308 L495,1178 L628,1292 L765,1140 L890,1260 L1035,1120 L1170,1240 L1308,1100 L1408,1192 L1440,1162 L1440,1600 L0,1600 Z" fill="url(#g-mtn-mid)" />
    <path d="M0,1440 L65,1295 L148,1350 L255,1218 L375,1308 L495,1178 L628,1292 L765,1140 L890,1260 L1035,1120 L1170,1240 L1308,1100 L1408,1192 L1440,1162" fill="none" stroke={c.warm} strokeWidth="1.0" strokeOpacity="0.20" />
    <path d="M0,1585 L45,1488 L108,1528 L192,1432 L295,1490 L395,1392 L508,1468 L628,1350 L750,1450 L870,1330 L1005,1432 L1128,1308 L1268,1408 L1370,1328 L1440,1372 L1440,1600 L0,1600 Z" fill="url(#g-mtn-near)" />
    <path d="M0,1585 L45,1488 L108,1528 L192,1432 L295,1490 L395,1392 L508,1468 L628,1350 L750,1450 L870,1330 L1005,1432 L1128,1308 L1268,1408 L1370,1328 L1440,1372" fill="none" stroke={c.warm} strokeWidth="1.4" strokeOpacity="0.28" />

    {[[570,970,68],[850,945,62],[1130,935,68],[628,1292,52],[870,1330,48]].map(([px,py,w],i) => (
      <g key={i}>
        <path d={`M${px-w},${py+52} Q${px-w*0.3},${py+22} ${px},${py} Q${px+w*0.3},${py+22} ${px+w},${py+52}`} fill="none" stroke={c.snowStroke} strokeWidth="1.0" strokeOpacity="0.28" />
        <path d={`M${px-w*0.55},${py+30} Q${px},${py} ${px+w*0.55},${py+30}`} fill={c.snowFill} fillOpacity="0.10" />
      </g>
    ))}

    <rect x="0" y="1550" width="1440" height="80" fill={c.zoneFade} fillOpacity="0.55" />

    {/* ══ FOREST / FIELD ZONE  y 1500 – 2400 ══ */}
    <rect x="0" y="1500" width="1440" height="900" fill="url(#g-forest-bg)" />
    <ellipse cx="720" cy="1620" rx="820" ry="220" fill="url(#g-forest-glow)" />

    <path d="M0,1950 Q140,1835 290,1875 Q440,1915 590,1815 Q740,1715 890,1775 Q1040,1835 1190,1755 Q1320,1685 1440,1730 L1440,2400 L0,2400 Z" fill="url(#g-hill-far)" />
    <path d="M0,2075 Q170,1975 340,2030 Q510,2085 680,1990 Q850,1895 1020,1975 Q1190,2055 1360,1975 Q1420,1950 1440,1960 L1440,2400 L0,2400 Z" fill="url(#g-hill-near)" fillOpacity="0.88" />

    {Array.from({ length: 40 }, (_, i) => {
      const x = 12 + i * 36 + (i % 4) * 9 - (i % 6) * 5;
      const y = 1978 + Math.sin(i * 1.05) * 18 + (i % 5) * 10;
      const h = 58 + (i % 6) * 14;
      const w = 16 + (i % 5) * 6;
      const op = (0.62 + (i % 4) * 0.08) * c.treeOpa;
      return (
        <g key={i} fillOpacity={op}>
          <rect x={x - 2.5} y={y + h * 0.68} width={5} height={h * 0.32} fill={c.treeTrunk} />
          <polygon points={`${x},${y} ${x+w},${y+h*0.36} ${x-w},${y+h*0.36}`} fill={c.tree1} />
          <polygon points={`${x},${y+h*0.22} ${x+w*0.88},${y+h*0.56} ${x-w*0.88},${y+h*0.56}`} fill={c.tree2} />
          <polygon points={`${x},${y+h*0.43} ${x+w*0.72},${y+h*0.74} ${x-w*0.72},${y+h*0.74}`} fill={c.tree3} />
        </g>
      );
    })}

    <path d="M0,2018 Q90,1975 180,2000 Q270,2025 360,1980 Q450,1935 540,1965 Q630,1995 720,1955 Q810,1915 900,1948 Q990,1980 1080,1938 Q1170,1896 1260,1930 Q1380,1878 1440,1905" fill="none" stroke={c.lime} strokeWidth="1.1" strokeOpacity="0.22" />
    <path d="M0,2295 Q190,2255 380,2272 Q570,2290 760,2262 Q950,2234 1140,2262 Q1330,2290 1440,2270 L1440,2400 L0,2400 Z" fill={c.meadow} fillOpacity="0.90" />

    <rect x="0" y="2345" width="1440" height="90" fill={c.zoneFade} fillOpacity="0.55" />

    {/* ══ LAKE ZONE  y 2300 – 3200 ══ */}
    <rect x="0" y="2300" width="1440" height="900" fill="url(#g-lake-bg)" />
    <rect x="0" y="2465" width="1440" height="735" fill="url(#g-lake-water)" />

    <ellipse cx="340" cy="2385" rx="120" ry="120" fill="url(#g-moon)" className="bg-moon-glow" />
    <circle cx="340" cy="2385" r="28" fill={c.moonDiscC1} />
    <circle cx="340" cy="2385" r="19" fill={c.moonDiscC2} />
    <circle cx="340" cy="2385" r="11" fill={c.moonCore} />

    <path d="M0,2450 Q180,2428 360,2438 Q540,2448 720,2430 Q900,2412 1080,2428 Q1260,2444 1440,2428 L1440,2480 L0,2480 Z" fill={c.farShore} fillOpacity="0.72" />
    <path d="M0,2478 Q120,2460 240,2470 Q400,2484 560,2464 Q720,2444 880,2462 Q1040,2480 1200,2460 Q1340,2442 1440,2460" fill="none" stroke={c.foam} strokeWidth="1.4" strokeOpacity="0.28" />

    <g className="bg-wave-drift">
      {[0,1,2,3,4,5,6,7,8].map(i => {
        const y = 2524 + i * 76;
        const amp = 11 + i * 2;
        return (
          <path key={i}
            d={`M-80,${y} C160,${y-amp} 400,${y+amp} 640,${y} C880,${y-amp} 1120,${y+amp} 1360,${y} C1440,${y-amp} 1500,${y+amp} 1560,${y}`}
            fill="none" stroke={c.wave} strokeWidth={0.85 - i * 0.045} strokeOpacity={0.20 - i * 0.013}
          />
        );
      })}
    </g>

    <ellipse cx="340" cy="2560" rx="7" ry="68" fill={c.moonReflect} fillOpacity="0.10" className="bg-moon-glow" />
    <ellipse cx="340" cy="2620" rx="28" ry="7" fill={c.moonReflect} fillOpacity="0.07" />
    <ellipse cx="340" cy="2680" rx="52" ry="5" fill={c.moonReflect} fillOpacity="0.05" />
    <ellipse cx="340" cy="2740" rx="80" ry="4" fill={c.moonReflect} fillOpacity="0.03" />

    {[175,340,520,710,880,1060,1225,1390].map((x, i) => (
      <ellipse key={i} cx={x} cy={2548 + (i % 5) * 58} rx={5 + (i % 3) * 3} ry={1.8}
        fill={c.shimmer} fillOpacity={0.12 + (i % 3) * 0.04}
        className="bg-shimmer" style={{ animationDelay: `${(i * 0.55) % 4}s` }} />
    ))}

    <path d="M0,2840 Q360,2812 720,2820 Q1080,2828 1440,2808 L1440,2860 L0,2860 Z" fill={c.farDistShore} fillOpacity="0.65" />
  </svg>
);

// ─── Fixed subtle scene components (other pages) ────────────────────────────

const Cloud = ({ top, scale, opacity, dur, delay, variant = "a" }) => (
  <div
    data-bg-anim=""
    style={{
      position: "absolute",
      top,
      left: 0,
      width: `${160 * scale}px`,
      height: `${55 * scale}px`,
      opacity,
      animation: `bg-drift-${variant} ${dur}s linear ${delay}s infinite`,
      willChange: "transform",
    }}
  >
    <svg width={160 * scale} height={55 * scale} viewBox="0 0 160 55" overflow="visible" style={{ display: "block" }}>
      <g fill="none" stroke="currentColor" strokeWidth="1.2">
        <ellipse cx="80" cy="34" rx="72" ry="20" />
        <ellipse cx="48" cy="38" rx="42" ry="16" />
        <ellipse cx="114" cy="38" rx="46" ry="16" />
        <ellipse cx="72" cy="22" rx="38" ry="20" />
        <ellipse cx="100" cy="20" rx="34" ry="18" />
      </g>
    </svg>
  </div>
);

const MountainLayer = ({ fill, opacity, points, stroke, strokeOpacity = 0.2, strokeWidth = 1 }) => (
  <svg
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMax slice"
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity }}
  >
    <polygon points={points} fill={fill} />
    <polyline
      points={points.split(" L").join(" L").replace("L1440,900 L0,900 Z", "").replace("M", "")}
      fill="none"
      stroke={stroke || fill}
      strokeWidth={strokeWidth}
      strokeOpacity={strokeOpacity}
    />
  </svg>
);

// ─── Sky scene (/apps) ────────────────────────────────────────────────────────
const SkyScene = ({ light }) => (
  <>
    <div style={{ position: "absolute", inset: 0, opacity: light ? 0.55 : 0.48, background: light
      ? "linear-gradient(180deg, #cfe6f7 0%, #e3f1fb 55%, #f4f9fd 100%)"
      : "linear-gradient(180deg, #0d2b52 0%, #1a5fa8 40%, #4da8d8 72%, #b8dff0 100%)" }} />
    <div data-bg-anim="" style={{ position: "absolute", top: "8%", right: "16%", width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,237,160,0.35) 0%, rgba(255,200,80,0.1) 40%, transparent 70%)", animation: "bg-shimmer 9s ease-in-out infinite" }} />
    <div style={{ color: light ? "#7d97ad" : "#c8e8f5" }}>
      <Cloud top="8%"  scale={1.3} opacity={light ? 0.4 : 0.55} dur={72}  delay={0}    variant="a" />
      <Cloud top="17%" scale={0.85} opacity={light ? 0.3 : 0.38} dur={96}  delay={-30}  variant="b" />
      <Cloud top="5%"  scale={0.65} opacity={light ? 0.22 : 0.28} dur={118} delay={-55}  variant="a" />
      <Cloud top="25%" scale={1.0}  opacity={light ? 0.26 : 0.32} dur={82}  delay={-42}  variant="b" />
    </div>
  </>
);

// ─── Mountain scene (/agency) ─────────────────────────────────────────────────
const MountainScene = ({ light }) => (
  <>
    <div style={{ position: "absolute", inset: 0, opacity: light ? 0.5 : 0.52, background: light
      ? "linear-gradient(180deg, #e6f0fa 0%, #d8e8f5 42%, #f3e6d8 100%)"
      : "linear-gradient(180deg, #08192e 0%, #0d3060 32%, #1a5a8a 62%, #d97040 84%, #e8a870 100%)" }} />
    <div data-bg-anim="" style={{ position: "absolute", top: "10%", left: "5%", right: "5%", height: "38%", background: "radial-gradient(ellipse at 45% 40%, rgba(80,200,200,0.08) 0%, rgba(60,120,200,0.05) 50%, transparent 75%)", animation: "bg-shimmer 14s ease-in-out infinite" }} />
    <div style={{ color: light ? "#9ab0c4" : "#a0b4c8" }}>
      <Cloud top="24%" scale={1.1} opacity={light ? 0.28 : 0.15} dur={130} delay={0}   variant="a" />
      <Cloud top="30%" scale={0.7} opacity={light ? 0.22 : 0.12} dur={160} delay={-50} variant="b" />
    </div>
    <MountainLayer opacity={light ? 0.32 : 0.45} fill={light ? "#d2dded" : "#2a4a72"} points="M0,900 L0,540 L120,380 L240,460 L400,310 L580,440 L720,290 L900,410 L1040,320 L1200,430 L1320,350 L1440,420 L1440,900 Z" stroke={light ? "#b0c2da" : "#4a7aaa"} />
    <MountainLayer opacity={light ? 0.4 : 0.58} fill={light ? "#bccbe0" : "#1a3050"} points="M0,900 L0,660 L100,580 L220,510 L360,590 L480,490 L620,560 L740,470 L860,550 L1000,460 L1140,540 L1260,470 L1380,530 L1440,490 L1440,900 Z" stroke={light ? "#9db1cd" : "#3a6090"} />
    <MountainLayer opacity={light ? 0.5 : 0.70} fill={light ? "#a3b6d0" : "#0d1e34"} points="M0,900 L0,760 L80,700 L180,730 L280,670 L380,710 L480,660 L580,700 L680,650 L800,690 L920,640 L1060,690 L1160,650 L1300,700 L1380,660 L1440,680 L1440,900 Z" stroke={light ? "#8aa0bf" : "#2a5080"} strokeOpacity={0.3} />
  </>
);

// ─── Sea scene (/lab) ─────────────────────────────────────────────────────────
const SeaScene = ({ light }) => (
  <>
    <div style={{ position: "absolute", inset: 0, opacity: light ? 0.5 : 0.50, background: light
      ? "linear-gradient(180deg, #e3f3f8 0%, #d2ecf3 50%, #c2e6f0 100%)"
      : "linear-gradient(180deg, #06131f 0%, #0b2a42 30%, #0e4060 52%, #0d6e82 68%, #0a8fa4 82%, #08a8c2 100%)" }} />
    <div data-bg-anim="" style={{ position: "absolute", top: "8%", left: "70%", width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle, rgba(200,240,255,0.28) 0%, rgba(150,220,255,0.08) 45%, transparent 70%)", animation: "bg-shimmer 11s ease-in-out 2s infinite" }} />
    <div style={{ color: light ? "#7fb0c4" : "#80c8e0" }}>
      <Cloud top="35%" scale={1.0} opacity={light ? 0.24 : 0.12} dur={165} delay={0}   variant="a" />
      <Cloud top="29%" scale={0.65} opacity={light ? 0.2 : 0.1} dur={200} delay={-60} variant="b" />
    </div>
    {[0, 1, 2, 3].map((i) => {
      const y = 580 + i * 60;
      const amp = 16 + i * 4;
      return (
        <svg key={i} viewBox="0 0 1440 900" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: (light ? 0.25 : 0.35) + i * 0.05 }}>
          <path
            d={`M0,${y} C240,${y - amp} 480,${y + amp} 720,${y} C960,${y - amp} 1200,${y + amp} 1440,${y} L1440,900 L0,900 Z`}
            fill={light ? `rgba(150,${200 - i * 8},${224 - i * 6},${0.28 + i * 0.07})` : `rgba(10,${80 + i * 18},${96 + i * 10},${0.4 + i * 0.1})`}
          />
          <path
            d={`M0,${y} C240,${y - amp} 480,${y + amp} 720,${y} C960,${y - amp} 1200,${y + amp} 1440,${y}`}
            fill="none" stroke={light ? "#4aa6c8" : "#6FE7FF"} strokeWidth="0.8" strokeOpacity="0.2"
          />
        </svg>
      );
    })}
  </>
);

// ─── Default / fallback ───────────────────────────────────────────────────────
const DefaultScene = ({ light }) => (
  <div style={{ position: "absolute", inset: 0, background: light
    ? "linear-gradient(150deg, rgba(190,210,235,0.30) 0%, transparent 55%)"
    : "linear-gradient(150deg, rgba(15,35,60,0.35) 0%, transparent 55%)" }} />
);

// ─── Route map ────────────────────────────────────────────────────────────────
const FIXED_SCENES = {
  "/agency": MountainScene,
  "/apps":   SkyScene,
  "/lab":    SeaScene,
};

// ─── Background ───────────────────────────────────────────────────────────────
export const Background = () => {
  const { pathname } = useLocation();
  const { theme } = useTheme();
  const isHome = pathname === "/";
  const light = theme !== "dark";
  const FixedScene = FIXED_SCENES[pathname] ?? DefaultScene;

  if (isHome) {
    return (
      <>
        <style>{STYLE}</style>
        {/* Scrolling landscape — sizes by SVG aspect ratio, scrolls with page */}
        <div
          aria-hidden="true"
          className="pointer-events-none"
          style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: -10 }}
        >
          <Landscape c={light ? LIGHT : DARK} />
        </div>
        {/* Fixed viewport overlays */}
        <div aria-hidden="true" className="pointer-events-none fixed inset-0" style={{ zIndex: -9 }}>
          <div className="absolute inset-0" style={{ background: "radial-gradient(900px 480px at 12% -6%, color-mix(in srgb, var(--page-accent) 8%, transparent), transparent 60%)" }} />
          <div className="absolute inset-0 noise" />
        </div>
      </>
    );
  }

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <style>{STYLE}</style>
      <div className="absolute inset-0" style={{ background: "var(--bg-0)" }} />
      <FixedScene light={light} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(900px 480px at 12% -6%, color-mix(in srgb, var(--page-accent) 8%, transparent), transparent 60%)" }} />
      <div className="absolute inset-0 noise" />
    </div>
  );
};
